/**
 * Management > SAFE.
 *
 * The SAFE assessments read a graph — business critical technology → risk → capability → place in
 * the network → security function, with products hung off the capabilities — and none of it is
 * editable anywhere else in the toolbox. The framework_tool merge loaded the graph; this page is
 * how it is maintained afterwards.
 *
 * The mapping tables come first and the reference tables second, because the mappings are what
 * goes wrong: a capability with no `capability_to_pin` row never appears in the flow diagram, and a
 * risk with no `risk_to_capability` row scores nothing. The reference tables behind them change
 * rarely.
 *
 * The diagnostics banner sits above the tabs on purpose. `GET db/safe/diagnostics/` reports
 * `resolvable: false` when the data would score wrong — most usefully when `risk.impact` still
 * holds ids rather than impact names, which is invisible in any single table below but breaks every
 * assessment. Whoever is editing these tables is the person who needs to see it.
 *
 * The tab strip mirrors `AdminDataManagement`, including the `:section` URL parameter, so a link to
 * one table survives a reload.
 */
import {
  HbrIcon,
  HbrTab,
  HbrTabGroup,
  HbrTabPanel,
} from '@harbor/elements-react-standalone';
import { alertsWarningSmall, check } from '@harbor/elements/icons';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NavHistoryTracker from '../../../navigation/NavHistoryTracker';
import { useSafeDiagnostics } from '../../../hooks/safe-hooks';
import { useSalesforceSession } from '../../../hooks/useSalesforceSession';
import { useAdminPermissions } from '../Hooks/useAdminPermissions';
import { MODELS, SAFE_ADMIN_MODEL_KEYS, ModelConfig } from '../RbacConfig';
import { ADMIN_URL_SAFE } from '../PageStructure/admin-values';
import AdminTab from '../PageStructure/AdminTab';

/** Where the tab strip breaks from mappings into the tables they join. */
const FIRST_REFERENCE_KEY = 'safeBusinessTechnologies';

const SafeDiagnosticsBanner: React.FC = () => {
  // `db/safe/diagnostics/` sits behind `HasSalesforceSession` with the rest of the assessment API,
  // so it is not requested without one. Curating the tables below does not need Salesforce — a SAFE
  // Admin is not necessarily a Salesforce-connected SE — so the page still works, just without the
  // health check, and says so rather than showing an empty space where a warning belongs.
  const salesforceConnected = useSalesforceSession();
  const { data, isLoading, error } = useSafeDiagnostics({ enabled: salesforceConnected });

  if (!salesforceConnected) {
    return (
      <div className="mb-4 rounded border border-gray-15 bg-gray-5 p-3 text-sm text-gray-60">
        Connect Salesforce to see the SAFE data health check. The tables below are editable without
        it.
      </div>
    );
  }

  if (isLoading || error || !data) return null;

  const counts = Object.entries(data.counts || {});
  const unresolved = Object.entries(data.unresolved || {}).filter(([, value]) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'number') return value > 0;
    return Boolean(value);
  });

  return (
    <div
      className={`mb-4 rounded border p-3 ${
        data.resolvable ? 'border-gray-15 bg-gray-5' : 'border-danger bg-warning/10'
      }`}
    >
      <div className="mb-2 flex items-center gap-2 font-semibold text-gray-70">
        <HbrIcon
          asset={data.resolvable ? check : alertsWarningSmall}
          className={data.resolvable ? 'text-sec-green' : 'text-danger'}
        />
        {data.resolvable
          ? 'SAFE data resolves cleanly.'
          : 'SAFE data does not resolve — the assessments will score incorrectly until this is fixed.'}
      </div>

      {!!counts.length && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-60">
          {counts.map(([key, value]) => (
            <span key={key}>
              <span className="font-semibold">{value}</span> {key.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}

      {!!unresolved.length && (
        <ul className="mt-2 list-disc pl-5 text-sm text-gray-70">
          {unresolved.map(([key, value]) => (
            <li key={key}>
              <span className="font-semibold">{key.replace(/_/g, ' ')}:</span>{' '}
              {Array.isArray(value) ? value.slice(0, 10).join(', ') : String(value)}
              {Array.isArray(value) && value.length > 10 ? ` (+${value.length - 10} more)` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AdminSafe: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { section } = useParams();
  const { isVisibleTab } = useAdminPermissions();

  const safeModels: Record<string, ModelConfig> = {};
  SAFE_ADMIN_MODEL_KEYS.forEach((key) => {
    const model = MODELS[key];
    if (model) safeModels[key] = model;
  });

  const modelKeys = Object.keys(safeModels).filter((key) => isVisibleTab(safeModels[key]));

  const getInitialTab = () => {
    if (!!section && modelKeys.includes(section)) return section;

    // A deep link written against a tab the viewer cannot see falls back rather than blanking.
    const fromPath = modelKeys.find((key) => location.pathname.endsWith(`/${key}`));

    return fromPath || modelKeys[0];
  };

  const [currentTabKey, setCurrentTabKey] = useState<string>(getInitialTab());

  const handleTabClick = (key: string) => {
    setCurrentTabKey(key);
    navigate(`${ADMIN_URL_SAFE}/${key}`);
  };

  if (!modelKeys.length) {
    return (
      <>
        <h1 className="mt-0 pb-6 border-b border-gray-15 text-gray-60 font-semibold leading-5">
          SAFE
        </h1>
        <p className="mt-4 text-gray-60">
          You do not have permission to manage the SAFE mappings.
        </p>
      </>
    );
  }

  return (
    <>
      <NavHistoryTracker title={currentTabKey} />
      <h1 className="mt-0 pb-6 border-b border-gray-15 text-gray-60 font-semibold leading-5">
        SAFE
      </h1>

      <SafeDiagnosticsBanner />

      <HbrTabGroup primary className="my-4">
        {modelKeys.map((key) => (
          <HbrTab
            slot="nav"
            panel={key}
            key={key}
            onClick={() => handleTabClick(key)}
            active={key === currentTabKey}
            // A visual break between the mapping tabs and the reference tabs behind them.
            className={key === FIRST_REFERENCE_KEY ? 'ml-4 border-l border-gray-15 pl-4' : ''}
          >
            {safeModels[key].label}
          </HbrTab>
        ))}

        {modelKeys.map((key) => (
          <HbrTabPanel name={key} key={key}>
            {currentTabKey === key && <AdminTab model={safeModels[key]} />}
          </HbrTabPanel>
        ))}
      </HbrTabGroup>
    </>
  );
};

export default AdminSafe;
